import { c as _c } from "react/compiler-runtime";
import { CheckBoxSwitcher, FormSelect, Hint, Popover, PrimaryButton, TimePicker, toast } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import { updateFunnelDeadlineSettings } from 'modules/funnels/funnel/api/funnel-api';
import DeadlineSettingsExpirationInput from 'modules/funnels/funnel/components/deadline-settings-tab/deadline-settings-expiration-input';
import DeadlineSettingsRadioGroup from 'modules/funnels/funnel/components/deadline-settings-tab/deadline-settings-radio-group';
import DeadlineSettingsTabSkeleton from 'modules/funnels/funnel/components/deadline-settings-tab/deadline-settings-tab-skeleton';
import MobileSettingsHeader from 'modules/funnels/funnel/components/mobile-settings-header';
import { FunnelStepSettingTabsEnum } from 'modules/funnels/funnel/enums/funnel-step-setting-tabs-enum';
import { useFunnelDeadlineSettings } from 'modules/funnels/funnel/hooks/use-funnel-deadline-settings';
import { useFunnelStepsList } from 'modules/funnels/funnel/hooks/use-funnel-steps-list';
import { DeadlineSettingsEditInterface, DeadlineSettingsErrorsInterface } from 'modules/funnels/funnel/types/deadline-settings-interface';
import { FunnelStepInterface } from 'modules/funnels/funnel/types/funnel-step-interface';
import { getAvailableFunnelStepsData, getFunnelStepsToRedirectData } from 'modules/funnels/funnel/utils/deadline-settings-tab-utils';
import { FunnelId } from 'modules/funnels/types/funnel-interface';
import React, { useEffect, useState } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { fetchWithFieldErrors } from 'shared/utils/fetch-with-errors';
import { getSecondsWithTimeZone, getSecondsWithoutTimeZone } from 'shared/utils/time-utils';
import DeadlineSettingsItemLayout from './deadline-settings-item-layout';
import { QuestionMarkIcon, SaveIcon, WarningIcon } from '@systemeio/ui/icons';
const defaultValues: DeadlineSettingsEditInterface = {
  enabled: false,
  deadlinePageExpirationInDays: undefined,
  expirationFunnelStep: undefined,
  deadlineTime: null,
  deadlineFunnelSteps: []
};
const defaultErrors: DeadlineSettingsErrorsInterface = {
  fields: {
    enabled: '',
    deadlinePageExpirationInDays: '',
    expirationFunnelStep: '',
    deadlineTime: '',
    deadlineFunnelSteps: ''
  }
};
interface DeadlineSettingsTabProps {
  funnelId: FunnelId;
  funnelStep: FunnelStepInterface | null;
}
function DeadlineSettingsTab(t0) {
  const $ = _c(37);
  const {
    funnelId
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const {
    funnelStepsList
  } = useFunnelStepsList(funnelId);
  const {
    deadlineSettings,
    mutate
  } = useFunnelDeadlineSettings(funnelId);
  const [tempState, setTempState] = useState(defaultValues);
  const [errors, setErrors] = useState(defaultErrors);
  const [isExpirationTime, setIsExpirationTime] = useState(false);
  const [deadlineSeconds, setDeadlineSeconds] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  let t1;
  if ($[0] !== funnelStepsList) {
    t1 = getFunnelStepsToRedirectData(funnelStepsList);
    $[0] = funnelStepsList;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const funnelStepsToRedirectData = t1;
  let t2;
  if ($[2] !== funnelStepsList) {
    t2 = getAvailableFunnelStepsData(funnelStepsList);
    $[2] = funnelStepsList;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const availableFunnelStepsData = t2;
  let t3;
  if ($[4] !== deadlineSeconds || $[5] !== funnelId || $[6] !== mutate || $[7] !== t || $[8] !== tempState || $[9] !== user?.timezone) {
    t3 = async () => {
      ;
      try {
        setIsLoading(true);
        await fetchWithFieldErrors(async () => {
          const newData = {
            ...tempState,
            deadlineTime: getSecondsWithTimeZone(deadlineSeconds, user?.timezone)
          };
          const response = await updateFunnelDeadlineSettings(funnelId, newData);
          await mutate(data => {
            if (data) {
              return response.data;
            }
          }, false);
        }, setErrors);
        setIsLoading(false);
        toast.success(t("global.changes_saved"));
      } catch (t4) {
        const error = t4;
        setIsLoading(false);
        if (error instanceof BadRequest) {
          if (error.errors.common) {
            const commonErrors = error.errors.common;
            setErrors(prev => ({
              ...prev,
              common: commonErrors
            }));
          }
        } else {
          toast.error(t("global.error"));
        }
      }
    };
    $[4] = deadlineSeconds;
    $[5] = funnelId;
    $[6] = mutate;
    $[7] = t;
    $[8] = tempState;
    $[9] = user?.timezone;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleConfirm = t3;
  let t4;
  if ($[11] !== deadlineSettings || $[12] !== user?.timezone) {
    t4 = () => {
      if (deadlineSettings) {
        setTempState(prev_0 => ({
          ...prev_0,
          deadlineFunnelSteps: deadlineSettings.deadlineFunnelSteps.map(_temp),
          deadlinePageExpirationInDays: deadlineSettings.deadlinePageExpirationInDays ?? 0,
          deadlineTime: deadlineSettings.deadlineTime,
          enabled: deadlineSettings.enabled,
          expirationFunnelStep: deadlineSettings.expirationFunnelStep?.id
        }));
        if (deadlineSettings.deadlineTime) {
          setIsExpirationTime(true);
          setDeadlineSeconds(getSecondsWithoutTimeZone(deadlineSettings.deadlineTime, user?.timezone));
        }
      }
    };
    $[11] = deadlineSettings;
    $[12] = user?.timezone;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const t5 = user?.timezone;
  let t6;
  if ($[14] !== deadlineSettings || $[15] !== t5) {
    t6 = [deadlineSettings, t5];
    $[14] = deadlineSettings;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  useEffect(t4, t6);
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <MobileSettingsHeader type={FunnelStepSettingTabsEnum.deadlineSettings} />;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== availableFunnelStepsData || $[19] !== deadlineSeconds || $[20] !== deadlineSettings || $[21] !== errors.fields || $[22] !== funnelStepsList || $[23] !== funnelStepsToRedirectData || $[24] !== handleConfirm || $[25] !== isExpirationTime || $[26] !== isLoading || $[27] !== t || $[28] !== tempState || $[29] !== user?.dateFormat || $[30] !== user?.timeFormat) {
    t8 = deadlineSettings && funnelStepsList ? <><DeadlineSettingsItemLayout><div className="flex items-center justify-between "><CheckBoxSwitcher label={<Hint label={t("dashboard.funnel.deadline_settings_description")}>{t("dashboard.funnel.deadline_settings_enable")}</Hint>} labelClassName="lg:text-[15px] font-medium text-sm text-text-primary leading-6" selected={tempState.enabled} onChange={value => setTempState(prev_1 => ({
            ...prev_1,
            enabled: value
          }))} disabled={!deadlineSettings} /><PrimaryButton disabled={false} width="large" onClick={handleConfirm} isFetching={isLoading} type="submit" className="hidden lg:flex"><SaveIcon /><span className="hidden lg:flex">{t("dashboard.funnel.deadline_settings_save")}</span></PrimaryButton></div></DeadlineSettingsItemLayout><DeadlineSettingsItemLayout><div className="flex flex-col gap-10 lg:flex-row"><DeadlineSettingsExpirationInput labelClassName="lg:text-[15px] font-medium text-sm text-text-primary" onChange={value_0 => setTempState(prev_2 => ({
            ...prev_2,
            deadlinePageExpirationInDays: value_0
          }))} value={tempState.deadlinePageExpirationInDays || 0} isFetching={false} label={t("dashboard.funnel.deadline_settings_deadline_expiration_title")} required={true} wrapperClassName="flex-[40_1_0%]" error={errors.fields.deadlinePageExpirationInDays} /><FormSelect label={t("dashboard.funnel.deadline_settings_expiration_funnel_step_title")} labelClassName="lg:text-[15px] font-medium text-sm text-text-primary" value={tempState.expirationFunnelStep} data={funnelStepsToRedirectData} onChange={value_1 => setTempState(prev_3 => ({
            ...prev_3,
            expirationFunnelStep: value_1
          }))} required={true} containerClassName="flex-[60_1_0%]" error={errors.fields.expirationFunnelStep} /></div></DeadlineSettingsItemLayout><DeadlineSettingsItemLayout><div className="flex flex-col gap-5"><CheckBoxSwitcher label={<div className="text-text-primary flex items-center gap-1.5 text-sm font-medium leading-6 lg:text-[15px]">{t("dashboard.funnel.deadline_settings_expiration_time_title")}<Popover label={t("dashboard.funnel.deadline_settings_expiration_time_tooltip")}>{_temp2}</Popover></div>} selected={isExpirationTime} onChange={value_2 => {
            if (!value_2) {
              setDeadlineSeconds(null);
            }
            setIsExpirationTime(value_2);
          }} />{isExpirationTime && <TimePicker seconds={deadlineSeconds} onChangeSeconds={setDeadlineSeconds} userDateFormat={user?.dateFormat} userTimeFormat={user?.timeFormat} />}</div></DeadlineSettingsItemLayout><div className="flex flex-col gap-5"><div className="flex items-start gap-1.5"><span className="text-text-primary text-sm font-medium lg:text-[15px]">{t("dashboard.funnel.deadline_settings_available_funnel_steps_title")}</span><Popover label={t("dashboard.funnel.deadline_settings_available_funnel_steps_tooltip")}>{_temp3}</Popover></div><DeadlineSettingsRadioGroup options={availableFunnelStepsData} selected={tempState.deadlineFunnelSteps} onChange={value_3 => {
          const isSelected = tempState.deadlineFunnelSteps.includes(value_3);
          if (isSelected) {
            setTempState(prev_4 => ({
              ...prev_4,
              deadlineFunnelSteps: prev_4.deadlineFunnelSteps.filter(funnelStepId => funnelStepId !== value_3)
            }));
          } else {
            setTempState(prev_5 => ({
              ...prev_5,
              deadlineFunnelSteps: [...prev_5.deadlineFunnelSteps, value_3]
            }));
          }
        }} error={errors.fields.deadlineFunnelSteps} /></div><PrimaryButton disabled={false} width="small" onClick={handleConfirm} isFetching={isLoading} type="submit" className="max-w-fit lg:hidden"><SaveIcon /><span className="flex">{t("dashboard.funnel.deadline_settings_save")}</span></PrimaryButton></> : <DeadlineSettingsTabSkeleton />;
    $[18] = availableFunnelStepsData;
    $[19] = deadlineSeconds;
    $[20] = deadlineSettings;
    $[21] = errors.fields;
    $[22] = funnelStepsList;
    $[23] = funnelStepsToRedirectData;
    $[24] = handleConfirm;
    $[25] = isExpirationTime;
    $[26] = isLoading;
    $[27] = t;
    $[28] = tempState;
    $[29] = user?.dateFormat;
    $[30] = user?.timeFormat;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== errors.common) {
    t9 = errors.common && <>{errors.common.map(_temp4)}</>;
    $[32] = errors.common;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== t8 || $[35] !== t9) {
    t10 = <div className="mb-7 flex flex-col gap-10 px-[15px] lg:px-[15px]">{t7}{t8}{t9}</div>;
    $[34] = t8;
    $[35] = t9;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  return t10;
}
function _temp4(error_0, index) {
  return <p className="text-danger px-6 text-sm" key={index}>{error_0}</p>;
}
function _temp3() {
  return <WarningIcon className="h-[18px] w-[18px]" />;
}
function _temp2(show) {
  return <QuestionMarkIcon className={`${show ? "fill-brand-primary" : "fill-fill-primary"} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />;
}
function _temp(funnelStep) {
  return funnelStep.id;
}
export default DeadlineSettingsTab;